import { motion } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import {
  animationVariants,
  authThemeConfigs,
  authLogoConfigs,
  authFooterTexts,
  resolveAuthLogoUrl,
  type AuthTheme,
} from './constants';

interface AuthLayoutProps {
  children: React.ReactNode;
  variant?: 'login' | 'register';
  theme?: AuthTheme;
}

export function AuthLayout({ children, variant = 'login', theme = 'invol' }: AuthLayoutProps) {
  const themeConfig = authThemeConfigs[theme] || authThemeConfigs.invol;
  const logoConfig = authLogoConfigs[theme] || authLogoConfigs.invol;
  const footerText = authFooterTexts[theme] || authFooterTexts.invol;

  const gradientClass = variant === 'login' ? themeConfig.login : themeConfig.register;

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 -z-10">
        {/* Base gradient */}
        <motion.div
          className={`absolute inset-0 bg-gradient-to-br ${gradientClass}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        />

        {/* Dot pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle, currentColor 1px, transparent 1px)`,
            backgroundSize: '24px 24px',
          }}
        />

        {/* Radial glow effect */}
        <div
          className="absolute inset-0 opacity-50"
          style={{
            background: `radial-gradient(circle at 50% 30%, ${themeConfig.radialGlow} 0%, transparent 50%)`,
          }}
        />

        {/* Animated floating orbs */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full"
          style={{
            background: `radial-gradient(circle, ${themeConfig.orbPrimary} 0%, transparent 70%)`,
          }}
          animate={{
            x: [0, 30, 0],
            y: [0, -20, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full"
          style={{
            background: `radial-gradient(circle, ${themeConfig.orbSecondary} 0%, transparent 70%)`,
          }}
          animate={{
            x: [0, -25, 0],
            y: [0, 25, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>

      {/* Content container */}
      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-4 py-12">
        {/* Logo/branding */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Link to="/" className="group">
            {logoConfig.type === 'image' && logoConfig.src ? (
              <img
                src={resolveAuthLogoUrl(logoConfig.src)}
                alt={logoConfig.alt || 'Logo'}
                className={`${logoConfig.height || 'h-12'} w-auto transition-opacity group-hover:opacity-80`}
              />
            ) : (
              <h1
                className={`text-3xl font-bold bg-gradient-to-r ${logoConfig.gradient || 'from-blue-600 to-purple-600'} bg-clip-text text-transparent transition-all group-hover:opacity-80`}
              >
                {logoConfig.text || 'Invol'}
              </h1>
            )}
          </Link>
        </motion.div>

        {/* Main content */}
        <motion.div
          variants={animationVariants.cardEnter}
          initial="hidden"
          animate="visible"
          className="w-full"
        >
          {children}
        </motion.div>

        {/* Footer */}
        <motion.p
          className="mt-8 text-xs text-slate-400"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          {footerText}
        </motion.p>
      </div>
    </div>
  );
}
